<div class="lab-import-dialog">
  <div class="header-div d-flex justify-content-center">
    <h6 class="text-uppercase mb-0 text-center">{{ 'Import Results' | translate }}</h6>
  </div>

  <!-- Blocking progress overlay (DASH-2684): analyze fires two sequential AI/LLM calls and can take
  many seconds, so show a loader with a status message instead of an unlabelled spinner. Re-submit
  is already gated by the analyzing/committing guards in analyze()/commit(). -->
  @if (analyzing || committing) {
    <div class="import-progress" role="status" aria-live="polite">
      <app-rings-loader></app-rings-loader>
      <p class="import-progress-message">{{ loaderMessage | translate }}</p>
    </div>
  }

  @if (stepperConfig) {
    <app-stepper-container [config]="stepperConfig" (stepChanged)="onStepChanged($event)">
    </app-stepper-container>
  }
</div>

<!-- ============ STEP 1: UPLOAD ============ -->
<ng-template #uploadTpl>
  <div class="upload-drop full-width" (click)="fileInput.click()"
    (drop)="onFileSelected($event.dataTransfer!.files); $event.preventDefault()"
    (dragover)="$event.preventDefault()">
    <p><strong>{{ 'Drop CSV or XLSX file here' | translate }}</strong></p>
    <small>CSV, XLSX {{ 'less than' | translate }} {{ maxFileSizeMB }}MB</small>
    <input type="file" hidden #fileInput accept=".csv,.xlsx,.xls" (change)="onFileSelected($event)" />
  </div>

  @if (selectedFile) {
    <div class="file-list full-width">
      <mat-icon>insert_drive_file</mat-icon>
      <div class="file-details">
        <div>{{ selectedFile.name }}</div>
        <div class="progress-text">{{ (selectedFile.size / 1024).toFixed(1) }}KB</div>
      </div>
      <button mat-icon-button (click)="removeFile()"><mat-icon>close</mat-icon></button>
    </div>
  }
</ng-template>

<!-- ============ STEP 2: MAPPING-CONFIRM ============ -->
<ng-template #mappingTpl>
  @if (proposal && columnMap) {
    <!-- Number-format / dialect banner (AC 13). Read-only: detected client-side from the CSV and
    shown for reviewer confidence. Editing is intentionally deferred until the BE accepts a
    dialect override on commit (see DASH-2663) — an editable control here would silently no-op. -->
    @if (showDialectBanner) {
      <div class="dialect-banner">
        <mat-icon>tune</mat-icon>
        <span class="dialect-title">{{ 'Detected number format' | translate }}</span>
        @if (effectiveDelimiter) {
          <span class="dialect-pair">
            <span class="dialect-key">{{ 'Delimiter' | translate }}:</span>
            <span class="dialect-value">{{ delimiterDisplayValue | translate }}</span>
          </span>
        }
        @if (effectiveDecimalSeparator) {
          <span class="dialect-pair">
            <span class="dialect-key">{{ 'Decimal separator' | translate }}:</span>
            <span class="dialect-value">{{ effectiveDecimalSeparator }}</span>
          </span>
        }
      </div>
    }
    <!-- Sheet-orientation override (DASH-2694). Shown only when the BE proposal surfaces `orientation`
    (deploy-order safety): a BE predating the paired change omits it and this panel stays hidden. -->
    @if (orientationAvailable) {
      <section class="panel orientation-panel">
        <h3>{{ 'Sheet Orientation' | translate }}</h3>
        <div class="orientation-detected">
          <mat-icon>grid_on</mat-icon>
          <span class="orientation-key">{{ 'Detected sheet orientation' | translate }}:</span>
          <span class="orientation-value">{{ orientationLabel | translate }}</span>
          <!-- Per-section confidence (DASH-2728): orientation confidence shown on its own so the reviewer sees
          the orientation is what is uncertain, independent of the column-map/unit confidence chips below. -->
          <span class="confidence-chip" [class.low]="orientationLowConfidence">
            {{ orientationConfidencePercent }}%
            @if (orientationLowConfidence) {
              · {{ 'Low confidence' | translate }}
            }
          </span>
        </div>
        @if (orientationRequiresConfirmation) {
          <div class="orientation-warning">
            <mat-icon>warning</mat-icon>
            <span>{{ 'The detected orientation is uncertain. Please confirm it is correct before proceeding.' | translate }}</span>
          </div>
        }
        <!-- Manual override controls (transpose toggle / header-row / re-analyze), un-gated in DASH-2704
        now that the paired BE change (DASH-2703) binds `transposed`/`dataStartRowIndex` and forces the
        chosen orientation on re-analyze. A user override returns RequiresConfirmation=false, so it also
        clears the low-confidence confirm gate below. Kept behind orientationOverrideSupported so it can
        be re-gated cheaply if the BE contract regresses. -->
        @if (orientationOverrideSupported) {
          <mat-slide-toggle [(ngModel)]="overrideTransposed" (change)="onTransposeToggled()">{{ 'Data is transposed' | translate }}</mat-slide-toggle>
          <!-- DASH-2728: the number is the real HEADER row as it appears in the user's own file (1-based, so
          "row 5" = the 5th row they see), NOT a post-parse offset. AC9: shown for a header block AND for
          any layout the BE flagged for confirmation, so a misclassified banner block stays recoverable. -->
          @if (showDataStartControl) {
            <mat-form-field appearance="outline" class="data-start-row" subscriptSizing="dynamic"
              [matTooltip]="'The row your file uses as column headers (row 1 = the first row)' | translate">
              <mat-label>{{ 'Header row in file' | translate }}</mat-label>
              <input matInput type="number" min="1" max="10001" step="1"
                [ngModel]="overrideHeaderRow" (ngModelChange)="onDataStartRowChanged($event)" />
                <mat-hint>{{ 'Row 1 = the first row in your file' | translate }}</mat-hint>
              </mat-form-field>
            }
          }
          @if (orientationRequiresConfirmation) {
            <mat-checkbox [(ngModel)]="orientationAcknowledged" class="orientation-ack">
              {{ 'I confirm the sheet orientation is correct' | translate }}
            </mat-checkbox>
          }
        </section>
      }
      <!-- Blocking sample-match errors -->
      @if (proposal.sampleMatchErrors.length) {
        <div class="blocking-notice">
          <mat-icon>error</mat-icon>
          <div>
            <strong>{{ 'Sample match errors' | translate }}</strong>
            <ul>
              @for (e of proposal.sampleMatchErrors; track trackByIndex($index)) {
                <li>
                  {{ e.sampleNumber }} — {{ e.reason }}
                </li>
              }
            </ul>
          </div>
        </div>
      }
      <!-- Column mapping panel -->
      <section class="panel">
        <h3>{{ 'Column Mapping' | translate }}</h3>
        <div class="column-map-grid">
          @for (f of columnMapFields; track trackByIndex($index)) {
            <mat-form-field appearance="outline">
              <mat-label>{{ f.label | translate }}</mat-label>
              <mat-select [(ngModel)]="columnMap[f.key]">
                <mat-option [value]="''">{{ 'Unmapped' | translate }}</mat-option>
                @for (h of headers; track trackByIndex($index)) {
                  <mat-option [value]="h">{{ h }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>
        <!-- DASH-3159: Re-analyze re-runs detection with the CURRENT column map (and the chosen sheet
        orientation), so a corrected column drives fresh matched samples, analyte previews and unit
        mappings. Moved out of the Sheet Orientation section and renamed from "Re-analyze with this
        orientation" because it now resubmits the column map, not just the orientation. -->
        <div class="reanalyze-action">
          <button mat-stroked-button class="reanalyze-btn" (click)="reanalyze()" [disabled]="analyzing">
            {{ 'Re-analyze' | translate }}
          </button>
        </div>
      </section>
      <!-- Unit mapping panel (AC 14) -->
      @if (unitMappings.length) {
        <section class="panel">
          <h3>{{ 'Unit Mapping' | translate }}</h3>
          @for (u of unitMappings; track trackByIndex($index)) {
            <div class="unit-map-row"
              [class.blocking]="u.mappedUnit == null">
              <span class="source-unit">{{ u.sourceUnit }}</span>
              <mat-icon>arrow_forward</mat-icon>
              <mat-form-field appearance="outline">
                <mat-select [ngModel]="u.mappedUnit" (ngModelChange)="onUnitOverride(u, $event)">
                  <mat-option [value]="null">{{ 'Unmapped' | translate }}</mat-option>
                  @for (o of unitOptions; track trackByIndex($index)) {
                    <mat-option [value]="o.value">{{ o.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <span class="confidence-chip" [class.low]="u.lowConfidence">
                {{ (u.confidence * 100) | number:'1.0-0' }}%
                @if (u.lowConfidence) {
                  · {{ 'Low confidence' | translate }}
                }
              </span>
            </div>
          }
        </section>
      }
      <!-- Analyte -> agent panel, grouped per sample -->
      <section class="panel">
        <h3>{{ 'Map analyte to agent' | translate }}</h3>
        @for (sample of analytePreviews; track trackByIndex(si); let si = $index) {
          <div class="sample-group">
            <div class="sample-header">{{ 'Sample' | translate }} {{ sample.sampleNumber }}</div>
            @for (a of sample.analytes; track trackByIndex($index)) {
              <div class="analyte-row"
                [class.unmapped]="!a.mappedAgentId || a.mappedAgentId === emptyGuid">
                <span class="source-label">{{ a.sourceLabel }}</span>
                <mat-icon>arrow_forward</mat-icon>
                <mat-form-field appearance="outline">
                  <mat-select [ngModel]="a.mappedAgentId" (ngModelChange)="onAgentOverride(a, $event, si)">
                    <mat-option [value]="emptyGuid">{{ 'Unmapped' | translate }}</mat-option>
                    @for (o of agentOptionsBySample[si]; track trackByIndex($index)) {
                      <mat-option [value]="o.id">
                        {{ o.name }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <span class="confidence-chip" [class.low]="a.lowConfidence">
                  {{ (a.confidence * 100) | number:'1.0-0' }}%
                  @if (a.lowConfidence) {
                    · {{ 'Low confidence' | translate }}
                  }
                </span>
                @if (!a.mappedAgentId || a.mappedAgentId === emptyGuid) {
                  <span class="unmapped-tag">{{ 'Unmapped' | translate }}</span>
                }
              </div>
            }
          </div>
        }
      </section>
      <!-- Blank-correction toggle -->
      <section class="panel blank-correction">
        <mat-slide-toggle [(ngModel)]="applyBlankCorrection" [disabled]="blankCorrectionDisabled">
          {{ 'Apply blank correction' | translate }}
        </mat-slide-toggle>
        <span class="hint">{{ '{{count}} blank(s) detected in this batch' | translate:{ count: proposal.detectedBlankCount } }}</span>
      </section>
      <!-- Supplemental document (DASH-2720). Optional, unparsed source document (typically the lab's PDF) that
      is attached to the requisition ONLY after a successful commit (BE DASH-2719). Placed on the mapping
      step so it is picked before the Confirm action that commits. Type/size validation shows inline. -->
      <section class="panel supplemental-panel">
        <h3>{{ 'Supplemental Document' | translate }}</h3>
        <p class="supplemental-hint">
          {{ 'Optionally attach the lab’s source document (PDF or image). It is not parsed and is saved to the requisition only after a successful import.' | translate }}
        </p>
        <div class="upload-drop full-width" (click)="supplementalInput.click()"
          (drop)="onSupplementalFileSelected($event.dataTransfer!.files); $event.preventDefault()"
          (dragover)="$event.preventDefault()">
          <p><strong>{{ 'Drop a PDF or image here' | translate }}</strong></p>
          <small>PDF, PNG, JPG {{ 'less than' | translate }} {{ maxFileSizeMB }}MB</small>
          <input type="file" hidden #supplementalInput accept=".pdf,.png,.jpg,.jpeg"
            (change)="onSupplementalFileSelected($event)" />
          </div>
          @if (supplementalFileError) {
            <div class="supplemental-error">
              <mat-icon>error</mat-icon>
              <span>{{ supplementalFileError | translate:{ maxFileSizeMB: maxFileSizeMB } }}</span>
            </div>
          }
          @if (selectedSupplementalFile) {
            <div class="file-list full-width">
              <mat-icon>picture_as_pdf</mat-icon>
              <div class="file-details">
                <div>{{ selectedSupplementalFile.name }}</div>
                <div class="progress-text">{{ (selectedSupplementalFile.size / 1024).toFixed(1) }}KB</div>
              </div>
              <button mat-icon-button (click)="removeSupplementalFile()"><mat-icon>close</mat-icon></button>
            </div>
          }
        </section>
        <!-- Excluded DATA rows the AI structure decision dropped (DASH-2727 LabImportProposal.Warnings, "Row N: …").
        Surfaced right above the preview so a dropped row is visible pre-commit rather than silently missing
        from the table below — the preview shows only the surviving parsed rows (DASH-2728). Placed outside the
        preview section so it still shows when every row was excluded (empty preview). MUST-FIX 3: only genuine
        "Row N:" entries carry the "will NOT be imported" heading. -->
        @if (excludedRowWarnings.length) {
          <div class="warning-notice">
            <mat-icon>warning</mat-icon>
            <div>
              <strong>{{ 'Rows excluded during analysis' | translate }}</strong>
              <p class="warning-subtext">{{ 'These rows will NOT be imported. Adjust the header row above if a real data row was dropped.' | translate }}</p>
              <ul>
                @for (w of excludedRowWarnings; track trackByIndex($index)) {
                  <li>{{ w }}</li>
                }
              </ul>
            </div>
          </div>
        }
        <!-- General analysis notes that are NOT dropped rows (MUST-FIX 3): orientation-confidence prompts, an
        unidentified sample-number column, etc. These are instructions/observations, so they must NOT be
        labelled "will not be imported" — that would read to a compliance reviewer as a lost data row. -->
        @if (analysisWarnings.length) {
          <div class="warning-notice info">
            <mat-icon>info</mat-icon>
            <div>
              <strong>{{ 'Analysis warnings' | translate }}</strong>
              <ul>
                @for (w of analysisWarnings; track trackByIndex($index)) {
                  <li>{{ w }}</li>
                }
              </ul>
            </div>
          </div>
        }
        <!-- Data preview — ALL surviving parsed rows (DASH-2684), paginated client-side so the reviewer can verify
        the full file before committing without rendering thousands of DOM rows at once. Excluded rows are
        listed in the warnings callout above (they are not part of this table). -->
        @if (proposal.preview.length) {
          <section class="panel">
            <h3>{{ 'Data Preview' | translate }}</h3>
            <div class="preview-scroll">
              <table class="preview-table">
                <thead>
                  <!-- Above each raw source header, show the LEAP field it is currently mapped to
                  (from columnMap — AI proposal or manual override). Rendered as a stacked pair inside one
                  sticky header cell so the label and its raw header scroll and stick together; an unmapped
                  column shows no label. -->
                  <tr>
                    @for (h of headers; track trackByIndex($index)) {
                      <th>
                        @let mappedLabel = mappedFieldByHeader[h];
                        @if (mappedLabel) {
                          <span class="mapped-field-label">{{ mappedLabel | translate }}</span>
                        }
                        <span class="raw-header">{{ h }}</span>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of pagedPreviewRows; track trackByIndex($index)) {
                    <tr>
                      @for (cell of row; track trackByIndex($index)) {
                        <td>{{ cell }}</td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="preview-pager">
              <span class="preview-range">
                {{ 'Showing {{start}}–{{end}} of {{total}} parsed rows' | translate:{ start: previewRangeStart, end: previewRangeEnd, total: previewTotal } }}
              </span>
              <span class="preview-pager-controls">
                <button mat-icon-button type="button" [disabled]="!canPreviewPrev" (click)="previewPrevPage()"
                  [attr.aria-label]="'Previous' | translate">
                  <mat-icon>chevron_left</mat-icon>
                </button>
                <button mat-icon-button type="button" [disabled]="!canPreviewNext" (click)="previewNextPage()"
                  [attr.aria-label]="'Next' | translate">
                  <mat-icon>chevron_right</mat-icon>
                </button>
              </span>
            </div>
          </section>
        }
      }
    </ng-template>

    <!-- ============ STEP 3: REVIEW ============ -->
    <ng-template #reviewTpl>
      @if (response) {
        <!-- Outcome banner (DASH-2684): the summary must reflect what was actually INGESTED, never imply
        success when 0 rows persisted. Three mutually-exclusive states: failed / partial / success. -->
        @if (importFailedEntirely) {
          <div class="nothing-imported-banner">
            <mat-icon>block</mat-icon>
            <span>{{ 'Nothing was imported — all rows failed.' | translate }}</span>
          </div>
        }
        @if (importPartial) {
          <div class="partial-imported-banner">
            <mat-icon>warning</mat-icon>
            <span>{{ '{{ingested}} of {{parsed}} parsed row(s) ingested — {{errored}} row(s) had errors.' | translate:{ ingested: response.processedCount, parsed: response.loadedCount, errored: response.erroredCount } }}</span>
          </div>
        }
        @if (importSucceeded) {
          <div class="success-imported-banner">
            <mat-icon>check_circle</mat-icon>
            <span>{{ '{{ingested}} result(s) ingested successfully.' | translate:{ ingested: response.processedCount } }}</span>
          </div>
        }
        <!-- Counts are scoped to THIS import's persisted results (BE: DASH-2682). "Results Ingested" is the
        number that actually persisted; "Rows Parsed" is what was read from the file — the two are
        distinct. When nothing ingested, the tiles render muted so they cannot read as success. -->
        <div class="summary-tiles" [class.failed]="importFailedEntirely">
          <div class="tile primary"><span class="value">{{ response.processedCount }}</span><span class="label">{{ 'Results Ingested' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.loadedCount }}</span><span class="label">{{ 'Rows Parsed' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.erroredCount }}</span><span class="label">{{ 'Rows With Errors' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.missingCount }}</span><span class="label">{{ 'Missing Results' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.censoredCount }}</span><span class="label">{{ 'Censored Results' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.exceedanceCount }}</span><span class="label">{{ 'Exceedances' | translate }}</span></div>
          <div class="tile"><span class="value">{{ response.blankCorrectedCount }}</span><span class="label">{{ 'Blank-corrected' | translate }}</span></div>
        </div>
        @if (response.alertsSent > 0) {
          <p class="alerts-sent">
            {{ '{{count}} exceedance alert(s) sent' | translate:{ count: response.alertsSent } }}
          </p>
        }
        <!-- Only assert archival when something was actually imported — a fully-failed 400 (processedCount === 0)
        does not archive the file, so showing this note then would be inaccurate. -->
        @if (!importFailedEntirely) {
          <p class="archived-note">
            <mat-icon>archive</mat-icon>
            {{ 'Lab result file archived to the requisition.' | translate }}
          </p>
        }
        @if (response.errors.length || response.sampleMatchErrors.length) {
          <section class="panel">
            <h3>{{ 'Errors' | translate }}</h3>
            @if (response.sampleMatchErrors.length) {
              <table mat-table [dataSource]="response.sampleMatchErrors" class="error-table">
                <ng-container matColumnDef="sampleNumber">
                  <th mat-header-cell *matHeaderCellDef>{{ 'Sample' | translate }}</th>
                  <td mat-cell *matCellDef="let e">{{ e.sampleNumber }}</td>
                </ng-container>
                <ng-container matColumnDef="reason">
                  <th mat-header-cell *matHeaderCellDef>{{ 'Reason' | translate }}</th>
                  <td mat-cell *matCellDef="let e">{{ e.reason }}</td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="['sampleNumber', 'reason']"></tr>
                <tr mat-row *matRowDef="let row; columns: ['sampleNumber', 'reason']"></tr>
              </table>
            }
            @if (response.errors.length) {
              <ul class="error-list">
                @for (err of response.errors; track trackByIndex($index)) {
                  <li>{{ err }}</li>
                }
              </ul>
            }
          </section>
        }
        <!-- Commit-time warnings (DASH-2728 MUST-FIX 4): the BE surfaces rows it dropped while rebuilding at commit
        (e.g. a forced orientation that re-applied footer rows the analyze preview did not) in response.warnings.
        These were previously never rendered, so a row visible in the preview could silently vanish post-commit.
        Surface them so the reviewer sees exactly what the commit build excluded. -->
        @if (response.warnings.length) {
          <section class="panel">
            <h3>{{ 'Warnings' | translate }}</h3>
            <ul class="warning-list">
              @for (w of response.warnings; track trackByIndex($index)) {
                <li>{{ w }}</li>
              }
            </ul>
          </section>
        }
      }
    </ng-template>
